import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, FilePlus2, Files, FileSearch, Printer, Briefcase, Users2, Building2,
  Settings, Package, Sparkles, UserCircle2, LogOut, Store,
  ClipboardList, BarChart3, Mail, Layers, Hammer, Target, Pencil, Receipt,
  Wallet, FileText, Newspaper, ListChecks, ShieldCheck, FileSpreadsheet,
  ChevronLeft, ChevronRight, Menu, History, Home
} from "lucide-react";

// Round 90 terdecies: filtro negozio globale — l'admin (o responsabile in futuro)
// sceglie in cima da quale negozio vedere i dati, e viene persistito in localStorage.
// Le pagine leggono da qui e allegano ?negozio_id= alle chiamate rilevanti.
export const NEGOZIO_FILTER_EVENT = "negozio-filter-change";
export const getNegozioFilter = () => localStorage.getItem("negozio_filter") || "";
export const setNegozioFilter = (v) => {
  if (v) localStorage.setItem("negozio_filter", v);
  else localStorage.removeItem("negozio_filter");
  window.dispatchEvent(new CustomEvent(NEGOZIO_FILTER_EVENT, { detail: v }));
};

const NAV = [
  { section: "Principale", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin","venditore","user","gestore"] },
    { to: "/nuovopreventivo", label: "Nuovo Preventivo", icon: FilePlus2, roles: ["admin","venditore","user"] },
    { to: "/importa-preventivo", label: "Preventivo da concorrente", icon: FileSearch, roles: ["admin","venditore","user"] },
    { to: "/schede-pacchetti", label: "Schede Pacchetti", icon: Printer, roles: ["admin","venditore","user"] },
    { to: "/preventivi", label: "Preventivi", icon: Files, roles: ["admin","venditore","user","cliente"] },
    { to: "/crm", label: "CRM Lead", icon: Target, roles: ["admin","venditore"] },
    { to: "/configuratoreesigenze", label: "Configuratore Esigenze", icon: Sparkles, roles: ["admin","venditore","user"] },
    { to: "/dashboardvenditore", label: "Le mie Provvigioni", icon: Wallet, roles: ["venditore"] },
  ]},
  { section: "Progettazione", items: [
    { to: "/progetti", label: "Progetti CAD", icon: Pencil, roles: ["admin","venditore","user"] },
    { to: "/materials", label: "Materiali", icon: Package, roles: ["admin","venditore","user"] },
  ]},
  { section: "Cantieri", items: [
    { to: "/gestore-cantieri", label: "Gestione Cantieri", icon: Hammer, roles: ["admin","gestore","venditore","user"] },
    { to: "/portale-sub", label: "I miei cantieri", icon: Briefcase, roles: ["subappaltatore"] },
  ]},
  { section: "Cliente", items: [
    { to: "/portale-cliente", label: "Portale Cliente", icon: UserCircle2, roles: ["cliente"] },
  ]},
  { section: "Analisi", items: [
    { to: "/adminanalytics", label: "Analytics", icon: BarChart3, roles: ["admin","responsabile"] },
    { to: "/centrocosto", label: "Centro di Costo", icon: BarChart3, roles: ["admin"] },
    { to: "/adminreportbudget", label: "Report Budget", icon: Receipt, roles: ["admin"] },
  ]},
  { section: "Amministrazione", items: [
    { to: "/adminpacchetti", label: "Pacchetti & Voci", icon: Layers, roles: ["admin"] },
    { to: "/adminoptional", label: "Optional", icon: Sparkles, roles: ["admin"] },
    { to: "/adminvocibackoffice", label: "Voci Backoffice", icon: ClipboardList, roles: ["admin"] },
    { to: "/adminlistinifornitori", label: "Listini Fornitori", icon: FileSpreadsheet, roles: ["admin"] },
    { to: "/adminfasicommessa", label: "Fasi Commessa", icon: Hammer, roles: ["admin"] },
    { to: "/adminvenditori", label: "Venditori", icon: Users2, roles: ["admin"] },
    { to: "/adminsubappaltatori", label: "Subappaltatori", icon: Users2, roles: ["admin"] },
    { to: "/adminclienti", label: "Clienti", icon: UserCircle2, roles: ["admin"] },
    { to: "/adminnegozi", label: "Negozi", icon: Store, roles: ["admin"] },
    { to: "/admintemplateemail", label: "Template Email", icon: Mail, roles: ["admin"] },
    { to: "/admindocumentitemplate", label: "Documenti Aziendali", icon: FileText, roles: ["admin","venditore","gestore"] },
    { to: "/adminmaterialitemplate", label: "Materiali Template", icon: ListChecks, roles: ["admin"] },
    { to: "/adminscontorichieste", label: "Richieste Sconto", icon: ShieldCheck, roles: ["admin"] },
    { to: "/adminblog", label: "Blog (SEO)", icon: Newspaper, roles: ["admin"] },
    { to: "/adminlandingcms", label: "Homepage Pubblica", icon: Home, roles: ["admin"] },
    { to: "/adminutenti", label: "Utenti & Ruoli", icon: UserCircle2, roles: ["admin"] },
    { to: "/adminimpostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
    { to: "/adminaudittrail", label: "Audit Trail", icon: History, roles: ["admin"] },
  ]},
];

const COLOR_MAP = {
  teal:    { bg: "#0F766E", hover: "#115E59", accent: "#14B8A6", soft: "#F0FDFA" },
  blue:    { bg: "#1D4ED8", hover: "#1E40AF", accent: "#3B82F6", soft: "#EFF6FF" },
  emerald: { bg: "#047857", hover: "#065F46", accent: "#10B981", soft: "#ECFDF5" },
  violet:  { bg: "#6D28D9", hover: "#5B21B6", accent: "#8B5CF6", soft: "#F5F3FF" },
  amber:   { bg: "#B45309", hover: "#92400E", accent: "#F59E0B", soft: "#FFFBEB" },
  rose:    { bg: "#BE185D", hover: "#9F1239", accent: "#F43F5E", soft: "#FFF1F2" },
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const [azienda, setAzienda] = useState({ nome: "Inside Home", colore_primario: "teal", logo: null });
  const [alertCount, setAlertCount] = useState(0);
  // Round 90 terdecies: filtro globale per negozio (admin/responsabile)
  const [negozi, setNegozi] = useState([]);
  const [negozioSelezionato, setNegozioSelezionato] = useState(getNegozioFilter());
  const nav = useNavigate();
  const location = useLocation();
  const isEditorRoute = location.pathname.startsWith("/editor/");
  // Sidebar a scomparsa: auto-chiusa su mobile (<lg) e su editor (più spazio per CAD), apribile manualmente.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const [sidebarOpen, setSidebarOpen] = useState(!isEditorRoute && !isMobile);
  useEffect(() => {
    const m = typeof window !== "undefined" && window.innerWidth < 1024;
    setSidebarOpen(!isEditorRoute && !m);
  }, [isEditorRoute, location.pathname]);

  useEffect(() => {
    // Carica branding + dati azienda dalle Impostazioni (unico posto). Fallback su /dati-azienda per retro-compat.
    Promise.all([
      api.get("/impostazioni").catch(() => ({ data: {} })),
      api.get("/dati-azienda").catch(() => ({ data: {} })),
    ]).then(([impRes, daRes]) => {
      const imp = impRes.data || {};
      const da = daRes.data || {};
      setAzienda({
        ...da,
        nome: imp.marchio_commerciale || imp.ragione_sociale || da.nome || "Configuratore",
        logo: imp.logo || da.logo || "",
        colore_primario: imp.colore_primario || da.colore_primario || "teal",
        ragione_sociale: imp.ragione_sociale || da.nome || "",
        piva: imp.partita_iva || da.piva || "",
      });
    });
  }, []);

  // Polling alerts ogni 3 minuti (solo per ruoli che gestiscono cantieri)
  useEffect(() => {
    if (!user || !["admin", "venditore", "gestore"].includes(user.role)) return;
    const fetchAlerts = () => api.get("/dashboard-alerts").then(r => setAlertCount(r.data?.totale_alert_critici || 0)).catch(() => {});
    fetchAlerts();
    const t = setInterval(fetchAlerts, 180000);
    return () => clearInterval(t);
  }, [user]);

  // Round 90 terdecies: carica lista negozi per il filtro globale (solo admin/responsabile)
  useEffect(() => {
    if (!user || !["admin", "responsabile"].includes(user.role)) return;
    api.get("/negozi").then(r => setNegozi(r.data || [])).catch(() => {});
  }, [user]);

  const colors = COLOR_MAP[azienda.colore_primario] || COLOR_MAP.teal;
  const role = user?.role || "user";

  // apply css vars
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand", colors.bg);
    root.style.setProperty("--brand-hover", colors.hover);
    root.style.setProperty("--brand-accent", colors.accent);
    root.style.setProperty("--brand-soft", colors.soft);
  }, [colors]);

  return (
    <div className="flex min-h-screen bg-zinc-50 relative">
      {/* Backdrop mobile quando sidebar aperta */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Pulsante apertura sidebar quando chiusa */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-3 left-3 z-50 h-10 w-10 rounded-md text-white flex items-center justify-center shadow-lg hover:opacity-90"
          style={{ background: colors.bg }}
          title="Apri menù"
          data-testid="sidebar-toggle-open"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}

      {/* SIDEBAR (collassabile) — overlay fixed su mobile, sticky su desktop */}
      <aside
        className={`shrink-0 text-white flex flex-col h-screen transition-all duration-200 overflow-hidden z-40
          ${sidebarOpen ? "w-64 fixed lg:sticky inset-y-0 left-0 lg:left-auto" : "w-0"}
          lg:top-0`}
        style={{ background: colors.bg }}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            {azienda.logo ? (
              <img src={azienda.logo} alt="logo" className="h-8" />
            ) : (
              <div className="h-9 w-9 rounded bg-white/10 flex items-center justify-center font-bold text-lg">
                {(azienda.nome || "I")[0]}
              </div>
            )}
            <div className="truncate flex-1">
              <div className="text-[11px] uppercase tracking-wider text-white/60">Configuratore</div>
              <div className="text-sm font-semibold truncate">{azienda.nome}</div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded hover:bg-white/10 shrink-0"
              title="Chiudi menù"
              data-testid="sidebar-toggle-close"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
          {NAV.map((group) => {
            const visibleItems = group.items.filter((it) => it.roles.includes(role));
            if (!visibleItems.length) return null;
            return (
              <div key={group.section}>
                <div className="px-3 text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold mb-1.5">
                  {group.section}
                </div>
                <ul className="space-y-0.5">
                  {visibleItems.map((it) => (
                    <li key={it.to}>
                      <NavLink
                        to={it.to}
                        data-testid={`nav-${it.to.replace(/\//g, "")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                            isActive ? "bg-white/15 text-white" : "text-white/75 hover:bg-white/10 hover:text-white"
                          }`
                        }
                      >
                        <it.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{it.label}</span>
                        {it.to === "/dashboard" && alertCount > 0 && (
                          <span className="ml-auto bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full mono" data-testid="sidebar-alert-badge">{alertCount > 99 ? "99+" : alertCount}</span>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 text-xs">
          <div className="flex items-center gap-2 px-2 py-2 rounded-md bg-white/5">
            <UserCircle2 className="h-8 w-8" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{user?.name || "Utente"}</div>
              <div className="text-white/60 truncate capitalize">{role}</div>
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              className="p-1.5 rounded hover:bg-white/10"
              title="Esci"
              data-testid="btn-logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        {/* Round 90 terdecies: toolbar globale con filtro negozio (admin/responsabile) */}
        {user && ["admin", "responsabile"].includes(user.role) && negozi.length > 0 && (
          <div className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-4 py-2 flex items-center gap-2 shadow-sm">
            <Store className="h-4 w-4 text-zinc-500" />
            <label className="text-xs text-zinc-500 font-semibold uppercase tracking-widest">Filtro negozio:</label>
            <select
              value={negozioSelezionato}
              onChange={(e) => {
                setNegozioSelezionato(e.target.value);
                setNegozioFilter(e.target.value);
              }}
              className="text-xs border border-zinc-300 rounded px-2 py-1.5 bg-white cursor-pointer font-semibold min-w-[200px]"
              data-testid="filtro-negozio-globale"
            >
              <option value="">🌐 Tutti i negozi</option>
              {negozi.map((n) => (
                <option key={n.id} value={n.id}>🏬 {n.nome}</option>
              ))}
            </select>
            {negozioSelezionato && (
              <button
                onClick={() => { setNegozioSelezionato(""); setNegozioFilter(""); }}
                className="text-xs text-rose-600 hover:underline"
                data-testid="filtro-negozio-clear"
              >
                Azzera
              </button>
            )}
            <div className="text-[10px] text-zinc-400 ml-auto italic">Il filtro si applica a preventivi, commesse e report.</div>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
